/**
 * @fileoverview The row limit on a long list of lessons: the instructor's
 * Lessons page, the org dashboard's Lessons tab, a program's Sessions tab and
 * a viewer's held sessions.
 *
 * Those lists read oldest first (lib/lessonHistory), and a limited one opens
 * at the start of its range: with the past 30 days chosen, its first row is
 * the earliest session of those 30 days. "Show 25 more" under the rows
 * reveals the next batch below them, so the list keeps reading top to bottom
 * in the order the sessions were taught. 25 at a time by default, 50 or 100
 * on request. The limit is display only: a list's counts, totals, Select all
 * and export still cover every row its filters select.
 */

import { useMemo, useState } from "react";
import { Button } from "@/components/studio/primitives";
import { DEFAULT_PAGE_SIZE, PAGE_SIZES, firstRows } from "@/lib/lessonHistory";

/**
 * @param {Array<object>} rows the whole filtered list, oldest first.
 * @param {string} resetKey changes with the filters, so a new question opens
 *   at the start of its rows again.
 */
export function usePagedRows(rows, resetKey) {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [view, setView] = useState({ key: resetKey, count: DEFAULT_PAGE_SIZE });
  const count = view.key === resetKey ? view.count : pageSize;
  const { shown, more } = useMemo(() => firstRows(rows, count), [rows, count]);
  return {
    shown,
    more,
    total: (rows || []).length,
    pageSize,
    setPageSize: (size) => {
      setPageSize(size);
      setView({ key: resetKey, count: size });
    },
    showMore: () => setView({ key: resetKey, count: count + pageSize }),
  };
}

/**
 * The bar under a limited list: Show more on the left, the page size on the
 * right. Drawn only once the list is longer than the smallest page.
 *
 * @param {{ paged: ReturnType<typeof usePagedRows>, unit?: string,
 *   testId?: string, className?: string }} props `unit` names a row ("lesson",
 *   "session").
 */
export function MoreRowsBar({ paged, unit = "lesson", testId = "more-rows", className = "" }) {
  if (paged.total <= PAGE_SIZES[0]) return null;
  const next = Math.min(paged.pageSize, paged.more);
  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 ${className}`} data-testid={testId}>
      <div className="flex flex-wrap items-center gap-3">
        {paged.more > 0 && (
          <Button variant="ghost" icon="arrowDown" onPress={paged.showMore} data-testid={`${testId}-more`}>
            Show {next} more {next === 1 ? unit : `${unit}s`}
          </Button>
        )}
        <span className="font-mono text-[11px] uppercase tracking-widest text-[var(--text-tertiary)]" data-testid={`${testId}-shown`}>
          {paged.more > 0 ? `First ${paged.shown.length} of ${paged.total}` : `All ${paged.total} shown`}
        </span>
      </div>
      <div className="flex items-center gap-1.5" role="group" aria-label={`${unit[0].toUpperCase()}${unit.slice(1)}s per page`}>
        <span className="mr-1 font-mono text-[11px] uppercase tracking-widest text-[var(--text-tertiary)]">Per page</span>
        {PAGE_SIZES.map((size) => (
          <button
            key={size}
            type="button"
            onClick={() => paged.setPageSize(size)}
            aria-pressed={paged.pageSize === size}
            data-testid={`${testId}-size-${size}`}
            className={[
              "flex h-8 min-w-[42px] items-center justify-center rounded-full border px-3 font-mono text-[12px] tabular-nums transition-colors duration-200 active:scale-95",
              paged.pageSize === size
                ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]"
                : "border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
            ].join(" ")}
          >
            {size}
          </button>
        ))}
      </div>
    </div>
  );
}
